import { TOOLTIP_DISPLAY_TIME } from '@src/features/shared/community/components/chat/chatList/ChatItemHeader';

type ChildNodeWithReplace = ChildNode & { replaceWith?: (...nodes: (Node | string)[]) => void };

const nodeSupportsReplaceWith = (node: Node): node is ChildNodeWithReplace =>
  typeof (node as ChildNodeWithReplace).replaceWith === 'function';
const replaceNodeSafely = (targetNode: Node, newNode: Node): void => {
  if (nodeSupportsReplaceWith(targetNode)) {
    (targetNode as ChildNodeWithReplace).replaceWith(newNode);
  } else if (targetNode.parentNode) {
    targetNode.parentNode.replaceChild(newNode, targetNode);
  }
};

export const createOverlayElement = (parsedDocument: Document, chatId?: number): HTMLDivElement => {
  const overlayElement = parsedDocument.createElement('div');
  overlayElement.style.position = 'absolute';
  overlayElement.style.top = '0';
  overlayElement.style.left = '0';
  overlayElement.style.width = '100%';
  overlayElement.style.height = '100%';
  overlayElement.style.cursor = 'pointer';
  overlayElement.style.background = 'transparent';
  overlayElement.style.pointerEvents = 'all';
  overlayElement.style.zIndex = '10';
  overlayElement.style.touchAction = 'none';
  overlayElement.className = 'mention-overlay';
  overlayElement.setAttribute('chat-id', String(chatId));

  overlayElement.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
  });

  return overlayElement;
};

export const transformMentionsInHtml = (
  originalHtml: string,
  isLoggedIn: boolean,
  currentUserId?: number,
  currentUserType?: string,
  currentChatId?: number,
): string => {
  if (!originalHtml) {
    return '';
  }

  const parsedDocument = new DOMParser().parseFromString(originalHtml, 'text/html');

  parsedDocument.querySelectorAll<HTMLElement>('.mention').forEach((mentionEl) => {
    const rawId =
      mentionEl.getAttribute('id') ?? mentionEl.getAttribute('data-mention-id') ?? undefined;
    const mentionUserId = rawId != null ? Number(rawId) : undefined;

    let mentionedUserType: 'COMPANY_USER' | 'TENANT_USER' | null = null;
    const url = mentionEl.getAttribute('href') || '';
    if (url.includes('/company/')) {
      mentionedUserType = 'COMPANY_USER';
    } else if (url.includes('/tenant/')) {
      mentionedUserType = 'TENANT_USER';
    }

    const isAllId = mentionUserId === -1;
    const isAllByUrl = (url || '').toLowerCase().includes('/all');
    const isAllByText = (mentionEl.textContent || '').trim().toUpperCase() === '@ALL';
    const isAll = isAllId || isAllByUrl || isAllByText;

    const isMe =
      isAll ||
      (currentUserId != null &&
        mentionedUserType != null &&
        currentUserType === mentionedUserType &&
        currentUserId === mentionUserId);

    mentionEl.setAttribute('data-mention-type', isMe ? 'yellow' : 'blue');

    if (!mentionEl.classList.contains('mention')) {
      mentionEl.classList.add('mention');
    }

    if (mentionEl.tagName.toLowerCase() !== 'a') {
      const a = parsedDocument.createElement('a');
      a.className = mentionEl.className;
      a.innerHTML = mentionEl.innerHTML;
      a.target = '_blank';
      a.href = '#';

      Array.from(mentionEl.attributes).forEach((attr) => {
        if (attr.name.startsWith('data-') || attr.name === 'id') {
          a.setAttribute(attr.name, attr.value);
        }
      });

      replaceNodeSafely(mentionEl, a);
    } else {
      const a = mentionEl as HTMLAnchorElement;
      if (!a.href) {
        a.target = '_self';
        a.href = '#';
      }
    }

    if (!isLoggedIn) {
      const overlay = parsedDocument.createElement('div');
      overlay.style.position = 'absolute';
      overlay.style.top = '0';
      overlay.style.left = '0';
      overlay.style.width = '100%';
      overlay.style.height = '100%';
      overlay.style.cursor = 'pointer';
      overlay.style.background = 'transparent';
      overlay.style.pointerEvents = 'all';
      overlay.style.zIndex = '10';
      overlay.style.touchAction = 'none';
      overlay.className = 'mention-overlay';
      if (currentChatId != null) {
        overlay.setAttribute('chat-id', String(currentChatId));
      }

      const wrapper = parsedDocument.createElement('div');
      wrapper.style.position = 'relative';
      wrapper.style.display = 'inline-block';

      mentionEl.parentNode?.replaceChild(wrapper, mentionEl);
      wrapper.appendChild(mentionEl);
      wrapper.appendChild(overlay);
    }
  });

  return parsedDocument.body.innerHTML;
};

export const handleMentionClick = (
  event: React.MouseEvent<HTMLDivElement, MouseEvent>,
  chatId: number,
  setMentionClickedChatId: (chatId: number | null) => void,
  displayTooltip: (duration: number) => void,
) => {
  const target = event.target as HTMLElement;

  // ログインしているときはmentionクラスが捕まる
  if (target.classList.contains('mention')) {
    event.stopPropagation();
    return;
  }

  // ログインしていないときはmention-overlayクラスが捕まる
  if (
    target.classList.contains('mention-overlay') &&
    target.getAttribute('chat-id') === String(chatId)
  ) {
    event.stopPropagation();
    setMentionClickedChatId(chatId);
    displayTooltip(TOOLTIP_DISPLAY_TIME);
  }
};
